<template>
  <div class="muf-field" :class="{ 'muf-field--error': hasError }">
    <label v-if="label" class="muf-label" :for="inputId">
      {{ label }}<span v-if="required" class="muf-required">*</span>
    </label>

    <label class="muf-dropzone" :for="inputId">
      <span class="muf-icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
      </span>
      <span class="muf-copy">
        <span class="muf-title">{{ displayTitle }}</span>
        <span class="muf-subtitle">{{ displaySubtitle }}</span>
      </span>
      <span class="muf-action">{{ uploading ? 'Uploading...' : 'Choose' }}</span>
      <input
        ref="fileInput"
        class="muf-input"
        type="file"
        :id="inputId"
        :name="name"
        :accept="accept"
        :required="required"
        :disabled="uploading"
        @change="onFileChange"
      >
    </label>

    <div v-if="uploading" class="muf-progress">
      <div class="muf-progress-bar"></div>
    </div>

    <span v-if="hasError" class="muf-error">{{ errorMessage }}</span>
  </div>
</template>

<script>
export default {
  name: 'ModernUploadField',
  props: {
    label: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      required: true
    },
    value: {
      required: false
    },
    accept: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    uploading: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: 'Select a file to upload'
    }
  },
  data: () => ({
    selectedFileName: ''
  }),
  computed: {
    inputId () {
      return `muf-${this.name}`
    },
    displayTitle () {
      if (this.selectedFileName) return this.selectedFileName
      if (this.value) return 'File uploaded'
      return this.hint
    },
    displaySubtitle () {
      if (this.uploading) return 'Upload in progress'
      if (this.selectedFileName) return 'Ready to upload'
      if (this.value) return 'Current file is saved'
      return 'Tap to browse from your computer'
    }
  },
  methods: {
    onFileChange (event) {
      const file = event.target.files && event.target.files[0]
      this.selectedFileName = file ? file.name : ''
      this.$emit('file-selected', event)
    },
    clear () {
      this.selectedFileName = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    }
  }
}
</script>

<style>
.muf-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.muf-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #1a1a2e;
}

.muf-required {
  color: #c0392b;
  margin-left: 3px;
}

.muf-dropzone {
  min-height: 58px;
  border: 1.5px dashed #d0d0e0;
  border-radius: 9px;
  background: #fafafa;
  color: #1a1a2e;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.muf-dropzone:hover {
  border-color: #5b4fcf;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.08);
}

.muf-field--error .muf-dropzone {
  border-color: #c0392b;
  background: #fff6f4;
}

.muf-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
}

.muf-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}

.muf-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muf-subtitle {
  font-size: 12px;
  color: #8a8a9a;
}

.muf-action {
  height: 32px;
  border-radius: 7px;
  background: #5b4fcf;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 13px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  flex: 0 0 auto;
}

.muf-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.muf-progress {
  width: 100%;
  height: 3px;
  border-radius: 999px;
  background: #f0f0f5;
  overflow: hidden;
}

.muf-progress-bar {
  width: 45%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #5b4fcf, #9c8dff);
  animation: muf-progress 1.1s ease-in-out infinite;
}

.muf-error {
  font-size: 12px;
  color: #c0392b;
}

@keyframes muf-progress {
  0% { transform: translateX(-120%); }
  100% { transform: translateX(240%); }
}

@media (max-width: 720px) {
  .muf-dropzone {
    align-items: flex-start;
  }

  .muf-action {
    align-self: center;
  }
}
</style>



// WEBPACK FOOTER //
// ModernUploadField.vue?42982733